<script setup lang="ts">
    import { reactive, inject, onMounted } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SubscriptionAccess, SmsMessageListItem } from '../Subscription.access';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { formatMoney } from '@cwd/Shared/utils/formatMoney';
    import Loading from '@shared/components/Loading.vue';
    import { AlertTypes } from '@shared/components/bsAlerts';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import List from '@shared/components/List/List.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    const props = defineProps<{
        serviceSubscriptionId: string;
    }>();

    const state = reactive<{
        selectedSmsMessageId: string;
    }>({
        selectedSmsMessageId: '',
    });

    async function loadSmsMessages() {
        state.selectedSmsMessageId = '';
        await subscriptionAccess.getSmsMessages();
    }

    onMounted(() => {
        loadSmsMessages();
    });

    async function searchSmsTransactions() {
        if (!state.selectedSmsMessageId) return;

        await subscriptionAccess.getSmsPrePaidTransactions(state.selectedSmsMessageId);
    }

    function getTransactionTypeName(type: number): string {
        const typeNames: Record<number, string> = {
            1: 'Added Funds',
            2: 'SMS Cost',
            3: 'SMS Correction',
            4: 'SMS Aborted',
            5: 'SMS Reservation',
        };
        return typeNames[type] || 'Unknown';
    }

    function deleteTransaction(transaction: any) {
        subscriptionAccess
            .deletePrePaidTransaction(transaction.PrePaidTransactionId)
            .then(() => {})
            .catch((error) => {
                console.error('Delete failed:', error);
            });
    }
</script>

<template>
    <h3>SMS PrePaidTransaction</h3>

    <BsAlert
        v-if="!subscriptionAccess.state.loadingSmsMessages && (subscriptionAccess.state.smsMessages || []).length === 0"
        :type="AlertTypes.Info"
        :dismissible="false"
        content="No SMS messages found." />

    <div v-else class="form-group d-flex gap-sm align-items-end flex-wrap max-900">
        <div class="flex-1" style="min-width: 300px">
            <SingleSelect
                v-model="state.selectedSmsMessageId"
                label="Select SMS Message"
                :items="subscriptionAccess.state.smsMessages || []"
                select-prop="SmsMessageId"
                :displayProps="['Subject', 'AccountName', { key: 'Created', type: 'date' }]"
                placeholder="Select SMS message..."
                @update:modelValue="searchSmsTransactions"></SingleSelect>
        </div>
        <button type="button" class="btn btn-default" @click="loadSmsMessages" :disabled="subscriptionAccess.state.loadingSmsMessages">
            <span v-if="subscriptionAccess.state.loadingSmsMessages" class="spinner-border spinner-border-sm" role="status"></span>
            Refresh list
        </button>
    </div>

    <Loading :show="subscriptionAccess.state.loadingSmsPrePaidTransactions" loadingStyle="progress-bar" :fixed="true" />

    <template v-if="subscriptionAccess.state.smsPrePaidTransactions">
        <h4>SMS Message Information</h4>

        <div class="row">
            <div class="col-md-6">
                <p>
                    <span class="label-look d-block">Subject:</span>
                    {{ subscriptionAccess.state.smsPrePaidTransactions.SmsMessage.Subject }}
                </p>
            </div>
            <div class="col-md-6">
                <p>
                    <span class="label-look d-block">Account:</span>
                    {{ subscriptionAccess.state.smsPrePaidTransactions.SmsMessage.Account.Name }}
                </p>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <p>
                    <span class="label-look d-block">Created:</span>
                    {{ formatDateTime(subscriptionAccess.state.smsPrePaidTransactions.SmsMessage.Created) }}
                </p>
            </div>
            <div class="col-md-6">
                <p>
                    <span class="label-look d-block">Status:</span>
                    {{ subscriptionAccess.state.smsPrePaidTransactions.SmsMessage.Status }}
                </p>
            </div>
        </div>

        <h4>PrePaidTransactions ({{ subscriptionAccess.state.smsPrePaidTransactions.Transactions.length }})</h4>

        <BsAlert
            v-if="subscriptionAccess.state.smsPrePaidTransactions.Transactions.length === 0"
            :type="AlertTypes.Info"
            :dismissible="false"
            content="No transactions found for this SMS message." />

        <List
            v-else
            itemId="PrePaidTransactionId"
            displayAs="table"
            :items="subscriptionAccess.state.smsPrePaidTransactions.Transactions"
            :tableHeaders="['Type', 'Amount', 'Currency', 'Account', 'Created', 'Created By', 'Actions']"
            :hideToolbar="true"
            class="th2-text-right">
            <template #item="{ item }">
                <td>{{ getTransactionTypeName(item.Type) }}</td>
                <td :class="{ 'c-danger': item.Amount < 0, 'c-success': item.Amount > 0 }" class="text-right">
                    {{ formatMoney(item.Amount, item.CurrencyCode) }}
                </td>
                <td>{{ item.CurrencyCode }}</td>
                <td>
                    <template v-if="item.AccountName">{{ item.AccountName }}</template>
                    <template v-else>Subscription</template>
                </td>
                <td>{{ formatDateTime(item.Created) }}</td>
                <td>{{ item.UserName || 'Unknown' }}</td>
                <td>
                    <button
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="deleteTransaction(item)"
                        :disabled="subscriptionAccess.state.deletingPrePaidTransaction">
                        Delete
                    </button>
                </td>
            </template>
        </List>
    </template>
</template>
